/**
 * @module profile/components/portfolio/portfolio_gallery_tile
 */
import React, { PropTypes, Component } from 'react';
import { v4 } from 'node-uuid';
import { findDOMNode } from 'react-dom';
import { DragSource, DropTarget } from 'react-dnd';
import GalleryTile from 'common/components/gallery_tile';
import PortfolioEntryInfo from 'profile/components/portfolio/portfolio_entry_info';
import { TABLET_BREAKPOINT_PX } from 'common/variables';
import Icon from 'common/components/svg_icon';

/**
 * DragSource specification object
 */
const tileSource = {
  beginDrag(props) {
    return {
      item: props.item,
      index: props.index,
    };
  },

  canDrag(props) {
    // FIXME: re-ordering disabled for now
    return false;

    if (props.editMode) {
      return true;
    }

    return false;
  },

  endDrag(props, monitor) {
    const dropIndex = monitor.getItem().index;
    const dragIndex = props.index;

    props.moveItem(dragIndex, dropIndex);
  }
};

/**
 * DropTarget specification object
 */
const tileTarget = {
  drop(props, monitor, component) {
    const dragIndex = monitor.getItem().index;
    const dropIndex = props.index;

    // Don't replace items with themselves
    if (dragIndex === dropIndex) {
      return undefined;
    }

    // Determine rectangle on screen
    const dropBoundingRect = findDOMNode(component).getBoundingClientRect();

    // Get vertical middle
    const dropMiddleY = (dropBoundingRect.bottom - dropBoundingRect.top) / 2;

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Get pixels to the top
    const dropClientY = clientOffset.y - dropBoundingRect.top;

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (dragIndex < dropIndex && dropClientY < dropMiddleY) {
      return undefined;
    }

    // Dragging upwards
    if (dragIndex > dropIndex && dropClientY > dropMiddleY) {
      return undefined;
    }

    monitor.getItem().index = dropIndex;

    return undefined;
  }
};

/**
 * Portfolio gallery tile component that renders our general GalleryTile element
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
class PortfolioGalleryTile extends Component {
  constructor(props) {
    super(props);

    this.removeEntry = this.removeEntry.bind(this);
  }

  removeEntry(e, itemId) {
    e.stopPropagation();

    this.props.onDeleteEntry(itemId);
  }

  render() {
    const { item, onToggleEntryDetails, editMode, userAccess,
      isDragging, connectDragSource, connectDropTarget } = this.props;
    const opacity = isDragging ? 0 : 1;
    const images = item.image? [item.image] : [];
    let label = null;

    if (item.is_runnerup) {
      label = 'Finalist';
    } else if (item.is_winner) {
      label = 'Winner';
    }

    const overlayElements = [];

    if (editMode && userAccess.isOwner) {
      overlayElements.push(
        <button
          className="btn btn-icon-only remove-entry"
          type="button"
          key={v4()}
          onClick={e => this.removeEntry(e, item.id)}
        >
          <Icon className="svg-icon" icon="close" aria-hidden="true" />
        </button>
      );
    }

    overlayElements.push(
      (<PortfolioEntryInfo key={v4()} entry={item} />)
    );

    return connectDragSource(connectDropTarget(
      <div className="column">
        <GalleryTile
          images={images}
          label={label}
          onTileClicked={() => onToggleEntryDetails(item.id)}
        >
          { !isDragging && overlayElements }
        </GalleryTile>
      </div>
    ));
  }
}

/**
 * PropTypes.
 *
 * @property {array} items - array of portfolio items to display
 * @property {bool} editMode - boolean value defining if portfolio page is in edit mode
 * @property {object} userAccess - logged user's privileges
 * @property {func} onToggleEntryDetails - toggle portfolio item details
 * @property {func} onDeleteEntry- function to call when deleting portfolio
 * DnD specific
 * @property {bool} isDragging - boolean value telling us if item is being dragged
 * @property {number} index - index of element in the entries list
 * @property {func} moveItem - function to call when item is dropped
 * @property {func} connectDragSource - DnD higher order function
 * @property {func} connectDropTarget - DnD higher order function
 */
PortfolioGalleryTile.propTypes = {
  item: PropTypes.object,
  editMode: PropTypes.bool,
  userAccess: PropTypes.object,
  onToggleEntryDetails: PropTypes.func,
  onDeleteEntry: PropTypes.func,
  index: PropTypes.number.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  moveItem: PropTypes.func.isRequired,
};

/**
 * Collecting function for the DropTarget
 */
const collectDrop = connect => ({
  connectDropTarget: connect.dropTarget(),
});

/**
 * Collecting function for the DragSource
 */
const collectDrag = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
});

const wrappedInSource = DropTarget('PortfolioTile', tileTarget, collectDrop)(PortfolioGalleryTile);
export default DragSource('PortfolioTile', tileSource, collectDrag)(wrappedInSource);




// WEBPACK FOOTER //
// ./profile/components/portfolio/portfolio_gallery_tile.jsx